import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { getPlaceSuggestions } from './services/awsConfig';
import Notification from './components/Notification';
import TermsAcceptanceModal from './components/TermsAcceptanceModal';
import './styles/AutocompletePage.css';

const AutocompletePage = () => {
  const [address, setAddress] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchCount, setSearchCount] = useState(parseInt(localStorage.getItem('guestSearchCount') || '0'));
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    console.log('Component mounted - user:', user, 'loading:', loading, 'searchCount:', searchCount, 'localStorage value:', localStorage.getItem('guestSearchCount'));
  }, [user, loading]);

  const handleAddressChange = async (e) => {
    const query = e.target.value;
    setAddress(query);

    if (query.length > 2) { // Start fetching suggestions after typing 3 characters
      setIsLoading(true);
      try {
        const results = await getPlaceSuggestions(query);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setNotification({
          show: true,
          message: 'Error fetching address suggestions. Please try again.',
          type: 'error'
        });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
      } finally {
        setIsLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setAddress(suggestion);
    setSuggestions([]);
  };

  const handleUnitNumberChange = (e) => {
    setUnitNumber(e.target.value);
  };

  const resetSearchCount = () => {
    setSearchCount(0);
    localStorage.removeItem('guestSearchCount');
    console.log('Search count reset to 0');
  };

  const handleGoClick = () => {
    if (address.trim()) {
      // Track Go button click to Google Ads
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17753023513/go_button_click',
          'event_category': 'engagement',
          'event_label': address.trim(),
          'user_type': user ? 'authenticated' : 'guest'
        });
        console.log('🎯 Tracked Go button click to Google Ads');
      }
      
      console.log('handleGoClick - user:', user, 'loading:', loading, 'searchCount:', searchCount);
      
      // Don't proceed if auth is still loading
      if (loading) {
        console.log('Auth still loading, please wait');
        return;
      }
      
      // Check if terms have been accepted for first-time users
      const termsAccepted = localStorage.getItem('termsAccepted');
      if (!user && !termsAccepted) {
        // Store the intended navigation for after terms acceptance
        setPendingNavigation({ address: address.trim(), unitNumber: unitNumber.trim(), isGuest: !user });
        setShowTermsModal(true);
        return;
      }
      
      // Allow first search without auth, require auth for subsequent searches
      if (!user && searchCount >= 1) {
        console.log('Blocking search, redirecting to login');
        setNotification({
          show: true,
          message: 'You have used your free search. Please sign up or log in to continue analyzing properties.',
          type: 'info'
        });
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
          navigate('/login');
        }, 3000);
        return;
      }

      proceedWithAnalysis();
    }
  };

  const proceedWithAnalysis = () => {
    let fullAddress = address.trim();
    
    if (unitNumber.trim()) {
      // Parse address to find city, state, zipcode pattern
      const addressParts = fullAddress.split(',').map(part => part.trim());
      
      if (addressParts.length >= 3) {
        // Assume format: "Street, City, State Zipcode"
        // Insert unit number after street address (before city)
        addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
        fullAddress = addressParts.join(', ');
      } else if (addressParts.length === 2) {
        // Check if last part contains state and zipcode
        const lastPart = addressParts[addressParts.length - 1];
        const stateZipPattern = /^([A-Z]{2}|[A-Za-z\s]+)\s+\d{5}(-\d{4})?$/;
        
        if (stateZipPattern.test(lastPart)) {
          // Format: "Street, State Zipcode" - insert unit after street
          addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
          fullAddress = addressParts.join(', ');
        } else {
          // Format: "Street, City" - insert unit after street
          addressParts.splice(1, 0, `# ${unitNumber.trim()}`);
          fullAddress = addressParts.join(', ');
        }
      } else {
        // Fallback: append unit number at the end
        fullAddress = `${fullAddress}, # ${unitNumber.trim()}`;
      }
    }
    
    // Track guest searches
    if (!user) {
      const newCount = searchCount + 1;
      console.log('Incrementing search count from', searchCount, 'to', newCount);
      setSearchCount(newCount);
      localStorage.setItem('guestSearchCount', newCount.toString());
    }
    
    console.log('Full address:', fullAddress);
    
    navigate('/form', { state: { address: fullAddress, isGuest: !user } });
  };

  const handleTermsAccept = () => {
    localStorage.setItem('termsAccepted', 'true');
    setShowTermsModal(false);
    
    if (pendingNavigation) {
      // Reconstruct the full address with unit number if provided
      let fullAddress = pendingNavigation.address;
      if (pendingNavigation.unitNumber) {
        const addressParts = fullAddress.split(',').map(part => part.trim());
        if (addressParts.length >= 3) {
          addressParts.splice(1, 0, `# ${pendingNavigation.unitNumber}`);
          fullAddress = addressParts.join(', ');
        } else if (addressParts.length === 2) {
          const lastPart = addressParts[addressParts.length - 1];
          const stateZipPattern = /^([A-Z]{2}|[A-Za-z\s]+)\s+\d{5}(-\d{4})?$/;
          if (stateZipPattern.test(lastPart)) {
            addressParts.splice(1, 0, `# ${pendingNavigation.unitNumber}`);
            fullAddress = addressParts.join(', ');
          } else {
            addressParts.splice(1, 0, `# ${pendingNavigation.unitNumber}`);
            fullAddress = addressParts.join(', ');
          }
        } else {
          fullAddress = `${fullAddress}, # ${pendingNavigation.unitNumber}`;
        }
      }
      
      // Track guest searches
      if (!user) {
        const newCount = searchCount + 1;
        setSearchCount(newCount);
        localStorage.setItem('guestSearchCount', newCount.toString());
      }
      
      navigate('/form', { state: { address: fullAddress, isGuest: pendingNavigation.isGuest } });
      setPendingNavigation(null);
    }
  };

  const handleTermsDecline = () => {
    setShowTermsModal(false);
    setPendingNavigation(null);
    setNotification({
      show: true,
      message: 'You must accept the Terms of Service and Privacy Policy to use our analysis tool.',
      type: 'error'
    });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  return (
    <div className="autocomplete-page">
      {notification.show && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
        />
      )}
      
      <div className="search-container">
        <div className="hero-content">
          <h1>Find Your Next Investment Property</h1>
          <p className="subtitle">Calculate ROI for informational purposes</p>
          
          
          <div className="search-box">
            <div className="input-wrapper">
              <i className="search-icon">🔍</i>
              <input
                type="text"
                value={address}
                onChange={handleAddressChange}
                placeholder="Enter a property address..."
                className="search-input"
              />
              <input
                type="text"
                value={unitNumber}
                onChange={handleUnitNumberChange}
                placeholder="Unit #"
                className="unit-input-inline"
              />
              <button 
                onClick={handleGoClick}
                disabled={!address.trim()}
                className="go-button"
              >
                Go
              </button>
              {isLoading && <div className="loader"></div>}
            </div>
            
            {suggestions.length > 0 && (
              <ul className="suggestions-list">
                {suggestions.map((suggestion, index) => (
                  <li 
                    key={index} 
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="suggestion-item"
                  >
                    {suggestion}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      
      <div className="features-section">
        <div className="feature">
          <div className="feature-icon">📊</div>
          <h3>Estimated ROI</h3>
          <p>Get estimated return on investment calculations based on available property data</p>
        </div>
        <div className="feature">
          <div className="feature-icon">🏠</div>
          <h3>Property Analysis</h3>
          <p>Review property information with comprehensive metrics for research purposes</p>
        </div>
        <div className="feature">
          <div className="feature-icon">💰</div>
          <h3>Financial Planning</h3>
          <p>Review property analysis with detailed financial estimates for informational purposes</p>
        </div>
      </div>
      
      <TermsAcceptanceModal 
        isOpen={showTermsModal}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </div>
  );
};

export default AutocompletePage;
